<template>
  <label :for="computedInputId" v-if="labelName" class="input-text-label">{{ labelName }}</label>
  <InputGroup
    unstyled
    :class="[
      'input-group-primary',
      containerClasses,
      wrapperClass,
      { '!border-borders-border-white': focused },
    ]"
  >
    <InputGroupAddon unstyled class="input-group-addon">
      <i class="pi pi-user input-text-icon--base-16" v-if="leftIcon"></i>
    </InputGroupAddon>
    <InputText
      :id="computedInputId"
      :name="name"
      :placeholder="placeholder"
      :type="type"
      :disabled="disabled"
      v-model="localModelValue"
      unstyled
      class="input-text-primary"
      v-bind="$attrs"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <InputGroupAddon unstyled class="input-group-addon">
      <icon
        data="@icon/search.svg"
        width="16px"
        height="16px"
        class="w-full input-text-icon"
        :fill="false"
        v-if="!loading && searchIcon"
      />
      <i class="pi pi-spin pi-spinner input-text-icon--base-16" v-if="loading" />
    </InputGroupAddon>
  </InputGroup>
  <small id="label-help" class="input-text-help-label" v-if="extraInfo">{{ extraInfo }}</small>
</template>

<script setup lang="ts">
import { defineProps, computed, defineEmits, ref } from "vue";
import InputText from "primevue/inputtext";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";

// Component renders multiple root nodes (label + group + help text), so fallthrough
// attrs (e.g. maxlength) can't auto-inherit — forward $attrs onto the inner input instead.
defineOptions({ inheritAttrs: false });

const props = defineProps({
  id: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "text",
  },
  labelName: {
    tpye: String,
    default: "",
  },
  extraInfo: {
    tpye: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Tap to type",
  },
  wrapperClass: {
    type: [Boolean, String],
    default: false,
  },
  isValid: {
    type: Boolean,
    default: false,
  },
  leftIcon: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  searchIcon: {
    type: Boolean,
    default: false,
  },
  modelValue: String,
  modelModifiers: {
    type: Object,
    default: () => ({}),
  },
  disabled: Boolean,
  size: { type: String, default: "md" },
});
const focused = ref(false);
const emit = defineEmits(["update:modelValue", "focus", "blur", "click"]);

const handleFocus = (event: FocusEvent) => {
  emit("focus", event);
  focused.value = true;
};

const handleBlur = (event: FocusEvent) => {
  emit("blur", event);

  focused.value = false;
};

const localModelValue = computed({
  get: () => props.modelValue,
  set: value => {
    const next =
      props.modelModifiers.trim && typeof value === "string" ? value.trim() : value;
    emit("update:modelValue", next);
  },
});

const computedInputId = computed(() => props.id || props.name);

const containerClasses = computed(() => {
  return [
    `input-text-${props.size}`,
    {
      disabled: props.disabled,
      "input-group-primary--leftIcon": props.leftIcon,
    },
  ];
});
</script>
